import { useTranslation } from 'react-i18next';
import { HOUR_PX, type CalendarWeek } from '../../hooks/useGoogleCalendarWeek';

/** Just the geometry CalendarWeekGrid needs — so it renders any week source
 *  (Google Calendar preview or our appointments), not only a full CalendarWeek. */
export type WeekGridData = Pick<
  CalendarWeek,
  'allDayEvents' | 'title' | 'days' | 'dayFmt' | 'hours' | 'startHour' | 'gridHeight' | 'blocksForDay'
> & {
  /** Per-hour row height in px; defaults to HOUR_PX when omitted. */
  hourPx?: number;
};

/** The week-grid renderer, driven by the geometry of a useGoogleCalendarWeek-style
 *  source passed as `cal`. Used by SpecialistDashboard's calendar view. */

const K = 'specialistConfig.sources.google.weekPreview';

export function CalendarWeekGrid({
  cal,
  onBlockClick,
}: {
  cal: WeekGridData;
  /** When set, each event block is clickable and calls this with the block key. */
  onBlockClick?: (key: string) => void;
}) {
  const { t } = useTranslation();
  const todayKey = new Date().toDateString();
  const HP = cal.hourPx ?? HOUR_PX;
  return (
    <>
      {/* All-day events strip */}
      {cal.allDayEvents.length > 0 ? (
        <div className="border-b border-border px-3 py-2 space-y-1">
          {cal.allDayEvents.map((e) => (
            <div key={e.id} className="flex items-center gap-2 text-xs">
              <span className="rounded bg-primary/20 px-1.5 py-0.5 text-[10px] text-primary">
                {t(`${K}.allDay`)}
              </span>
              <span className="truncate text-foreground">{cal.title(e)}</span>
            </div>
          ))}
        </div>
      ) : null}

      {/* Day headers — flex widths mirror the body row below. */}
      <div className="flex sticky top-0 z-10 bg-card border-b border-border px-3">
        <div className="w-12 shrink-0" />
        {cal.days.map((d) => {
          const isToday = d.toDateString() === todayKey;
          return (
            <div
              key={d.toISOString()}
              className={`flex-1 py-1.5 text-center text-[11px] ${
                isToday ? 'font-semibold text-primary' : 'text-muted-foreground'
              }`}
            >
              {cal.dayFmt.format(d)}
            </div>
          );
        })}
      </div>

      {/* pt-3 gives the first hour label (centered on top:0 → otherwise its top
          half is clipped) room to render. */}
      <div className="flex px-3 pt-3">
        {/* Time axis */}
        <div className="relative w-12 shrink-0" style={{ height: cal.gridHeight }}>
          {cal.hours.map((h) => (
            <div
              key={h}
              className="absolute right-1.5 -translate-y-1/2 text-[10px] tabular-nums text-muted-foreground"
              style={{ top: (h - cal.startHour) * HP }}
            >
              {String(h).padStart(2, '0')}:00
            </div>
          ))}
        </div>

        {/* Day columns */}
        {cal.days.map((d) => (
          <div
            key={d.toISOString()}
            className="relative flex-1 border-l border-border"
            style={{ height: cal.gridHeight }}
          >
            {cal.hours.map((h) => (
              <div
                key={h}
                className="absolute left-0 right-0 border-t border-border/40"
                style={{ top: (h - cal.startHour) * HP }}
              />
            ))}
            {cal.blocksForDay(d).map((b) => {
              // Column-packing: overlapping events split the column width. Blocks
              // with no lane info (e.g. Google preview) render full width.
              const lanes = b.laneCount ?? 1;
              const lane = b.lane ?? 0;
              const laneStyle =
                lanes > 1
                  ? {
                      left: `calc(${((lane / lanes) * 100).toFixed(4)}% + 1px)`,
                      width: `calc(${(100 / lanes).toFixed(4)}% - 3px)`,
                    }
                  : undefined;
              return (
                <div
                  key={b.key}
                  className={`absolute overflow-hidden rounded border px-1 py-0.5 text-[9px] font-medium leading-tight text-foreground ${
                    lanes > 1 ? '' : 'left-0.5 right-0.5'
                  } ${b.color ?? 'border-primary/50 bg-primary/25'} ${onBlockClick ? 'cursor-pointer hover:brightness-95' : ''}`}
                  style={{ top: b.top, height: b.height, ...laneStyle }}
                  title={b.tip}
                  {...(onBlockClick
                    ? { role: 'button', tabIndex: 0, onClick: () => onBlockClick(b.key) }
                    : {})}
                >
                  {b.title}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </>
  );
}

